import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import {
  FiAward,
  FiArrowLeft,
  FiRefreshCw,
  FiSearch,
  FiCheckCircle,
  FiClock,
  FiAlertCircle,
  FiCalendar,
  FiUser,
  FiChevronRight,
  FiCheck,
  FiZap,
  FiRotateCcw,
  FiX,
  FiHash,
  FiAlertTriangle,
  FiMail,
  FiSend,
  FiBell,
  FiCheckSquare,
  FiSquare,
  FiUsers,
  FiLayers,
  FiMoreVertical,
} from "react-icons/fi";
import toast from "react-hot-toast";
import {
  getEventWinnersHistory,
  getEventParticipants,
  approveRewardStatus,
  revertAllEventRewards,
  approveAllEventRewards,
  sendWinnerEmail,
  sendWinnerPush,
  sendBulkWinnerPush,
  formatPushTemplate,
  fetchAllWinnersForEvent,
  getEvents,
  getEventById,
  syncEventWinnerRewards,
} from "../api/eventApi";
import { formatUtcDateTime } from "../utils/dateUtils";
import {
  calculateUserCoins,
  buildParticipantsMap,
  findMatchingParticipant,
  getItemRewardStatus,
  mergeHistoryWithLocalApprovals,
  setLocalRewardApproval,
  getMaxWinningRank,
} from "../utils/eventCoinUtils";

const STATUS_FILTERS = [
  { value: "all", label: "All Statuses" },
  { value: "pending", label: "Pending Approval", dot: "bg-amber-500" },
  { value: "credited", label: "Credited", dot: "bg-emerald-500" },
];

export const APP_ROUTES = [
  { label: "🏠 Home (Default)", value: "/home" },
  { label: "🏆 Leaderboard", value: "/leaderboard" },
  { label: "🚀 Splash", value: "/splash" },
  { label: "👥 Refer & Earn", value: "/refer" },
  { label: "🗺️ Zeta Journey", value: "/zeta-journey" },
  { label: "✨ Onboarding", value: "/onboarding" },
  { label: "🌐 Community", value: "/community" },
  { label: "👤 Account / Profile", value: "/account" },
  { label: "📝 Signup", value: "/signup" },
  { label: "➕ Add Refer", value: "/add-refer" },
  { label: "🔄 Update App", value: "/update-app" },
  { label: "⭐ Rate Us", value: "/rate" },
  { label: "📊 Dashboard", value: "/dashboard" },
  { label: "🆔 KYC Verification", value: "/kyc" },
  { label: "📢 Announcement", value: "/announcement" },
  { label: "🎮 All Games", value: "/game" },
  { label: "👆 Tap Rush Game", value: "/tap-rush" },
  { label: "🎰 777 Lucky Slot Game", value: "/lucky-slot" },
  { label: "🎡 Lucky Spinner Game", value: "/spinner" },
  { label: "🃏 Boom Cards Game", value: "/boom-cards" },
];

const EventWinners = () => {
  const { id: routeEventId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const activeEventId = routeEventId || searchParams.get("eventId") || "";
  const initialRewardFilter = searchParams.get("type") || "all";

  // State
  const [eventsList, setEventsList] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const [history, setHistory] = useState([]);
  const [participantsMap, setParticipantsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [summaryData, setSummaryData] = useState({});
  const [statusFilter, setStatusFilter] = useState("all");
  const [rewardFilter, setRewardFilter] = useState(initialRewardFilter); // "all" | "zx" | "crypto"
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debounceTimer = useRef(null);
  
  // Dropdown state for Action menu
  const [openDropdownId, setOpenDropdownId] = useState(null);

  useEffect(() => {
    const handleClickOutside = () => setOpenDropdownId(null);
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Search input handler with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setPage(1);
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(value);
    }, 400);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setDebouncedSearch("");
    setPage(1);
    clearTimeout(debounceTimer.current);
  };

  // Confirmation Modal State
  // modalState: null | { type: "APPROVE_CRYPTO" | "APPROVE_ZX" | "REVERT_ZX" | "REVERT_CRYPTO" | "REVERT_SINGLE" | "APPROVE_ALL" | "REVERT_ALL", target?: any }
  const [modalState, setModalState] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [txnIdInput, setTxnIdInput] = useState("");
  const [selectedRoute, setSelectedRoute] = useState("/home");
  const [urlInput, setUrlInput] = useState("");

  // Send Custom Email Modal State
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [selectedWinnerForEmail, setSelectedWinnerForEmail] = useState(null);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [emailRoute, setEmailRoute] = useState("/home");
  const [emailUrl, setEmailUrl] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  // Send Custom Push Notification Modal State
  const [isPushModalOpen, setIsPushModalOpen] = useState(false);
  const [selectedWinnerForPush, setSelectedWinnerForPush] = useState(null);
  const [pushTitle, setPushTitle] = useState("");
  const [pushMessage, setPushMessage] = useState("");
  const [pushRoute, setPushRoute] = useState("/home");
  const [pushUrl, setPushUrl] = useState("");
  const [sendingPush, setSendingPush] = useState(false);

  // Bulk Push Notification State
  const [isBulkPushModalOpen, setIsBulkPushModalOpen] = useState(false);
  const [bulkTargetAudience, setBulkTargetAudience] = useState("all"); // "all" | "pending" | "credited" | "selected"
  const [bulkPushTitle, setBulkPushTitle] = useState("");
  const [bulkPushMessage, setBulkPushMessage] = useState("");
  const [bulkPushRoute, setBulkPushRoute] = useState("/home");
  const [bulkPushUrl, setBulkPushUrl] = useState("");
  const [sendingBulkPush, setSendingBulkPush] = useState(false);
  const [bulkPushProgress, setBulkPushProgress] = useState({
    current: 0,
    total: 0,
    percent: 0,
    successCount: 0,
    failCount: 0,
    currentName: "",
  });
  const [selectedHistoryIds, setSelectedHistoryIds] = useState(new Set());
  const [syncingPrizes, setSyncingPrizes] = useState(false);

  // 1. Fetch list of available events for switcher
  useEffect(() => {
    const loadEvents = async () => {
      try {
        const res = await getEvents({ page: 1, limit: 100 });
        const list = res?.data?.events || (Array.isArray(res?.data) ? res.data : []);
        setEventsList(list);

        if (!activeEventId && list.length > 0) {
          const firstCompleted = list.find((e) => e.status === "completed") || list[0];
          const targetId = firstCompleted.eventId || firstCompleted.id;
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set("eventId", targetId);
            return next;
          });
        }
      } catch (err) {
        console.error("Failed to load events list", err);
      }
    };
    loadEvents();
  }, [activeEventId, setSearchParams]);

  // 2. Fetch specific event details
  useEffect(() => {
    if (!activeEventId) return;
    const loadEventDetails = async () => {
      try {
        const res = await getEventById(activeEventId);
        setSelectedEvent(res?.data || res);
      } catch (err) {
        console.warn("Failed to load event details", err);
      }
    };
    loadEventDetails();
  }, [activeEventId]);

  // 3. Fetch winners history & participants
  const fetchHistory = useCallback(async () => {
    if (!activeEventId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      try {
        await syncEventWinnerRewards(activeEventId);
      } catch (syncErr) {
        console.warn("Winner prize sync:", syncErr?.message || syncErr);
      }

      const [historyRes, participantsRes] = await Promise.allSettled([
        getEventWinnersHistory(activeEventId, {
          page,
          limit,
          rewardStatus: statusFilter,
          search: debouncedSearch,
        }),
        getEventParticipants(activeEventId, {
          page: 1,
          limit: 200,
        }),
      ]);

      if (historyRes.status === "fulfilled") {
        const responseData = historyRes.value?.data || historyRes.value;
        const historyList =
          responseData?.history || (Array.isArray(responseData) ? responseData : []);
        const pagination = responseData?.pagination || {};
        if (responseData?.summary) {
          setSummaryData(responseData.summary);
        }

        const maxWinners = getMaxWinningRank(selectedEvent);
        const actualTotalWinners = Math.min(
          pagination.total ?? historyList.length,
          maxWinners
        );

        const mergedList = mergeHistoryWithLocalApprovals(historyList);
        setHistory(mergedList);
        setTotalRecords(actualTotalWinners);
        const calculatedPages =
          Math.ceil(actualTotalWinners / limit) || 1;
        setTotalPages(calculatedPages);
      } else {
        throw new Error(
          historyRes.reason?.message || "Failed to fetch event winners history"
        );
      }

      if (participantsRes.status === "fulfilled") {
        const pData = participantsRes.value?.data || participantsRes.value;
        const pList =
          pData?.participants ||
          pData?.history ||
          (Array.isArray(pData) ? pData : []);
        setParticipantsMap(buildParticipantsMap(pList));
      }
    } catch (err) {
      setError(err.message || "Failed to fetch event winners history");
    } finally {
      setLoading(false);
    }
  }, [activeEventId, page, limit, statusFilter, debouncedSearch, selectedEvent]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleSyncPrizesFromEvent = async () => {
    if (!activeEventId) return;
    setSyncingPrizes(true);
    try {
      const res = await syncEventWinnerRewards(activeEventId);
      const count = res?.data?.updatedCount ?? res?.updatedCount;
      const skipped = res?.data?.skippedLocked ?? res?.skippedLocked;
      toast.success(
        res?.message ||
          `Synced prize tiers${count != null ? ` (${count} row(s))` : ""}${
            skipped != null ? ` — ${skipped} already paid, left unchanged` : ""
          }`
      );
      await fetchHistory();
    } catch (err) {
      toast.error(err?.message || "Failed to sync prizes to winner records");
    } finally {
      setSyncingPrizes(false);
    }
  };

  // Event Switcher handler
  const handleEventChange = (e) => {
    const newId = e.target.value;
    if (newId) {
      setPage(1);
      setSearchTerm("");
      setDebouncedSearch("");
      clearTimeout(debounceTimer.current);
      if (routeEventId) {
        navigate(`/events/${newId}/winners`);
      } else {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set("eventId", newId);
          return next;
        });
      }
    }
  };

  // Filter history based on reward type and search term
  const cryptoList = history.filter((h) => {
    const s = getItemRewardStatus(h);
    return s.hasCoinReward && s.coinAmount > 0;
  });
  const zxList = history.filter((h) => {
    const s = getItemRewardStatus(h);
    return s.zxAmount > 0;
  });

  const maxWinners = getMaxWinningRank(selectedEvent);

  const filteredHistory = history.filter((item, idx) => {
    const stats = getItemRewardStatus(item);
    const itemRank = Number(
      item.userRank ?? item.rank ?? (page - 1) * limit + idx + 1
    );

    // If not searching, strictly filter out users with rank > maxWinners
    if (!searchTerm.trim() && itemRank > maxWinners) {
      return false;
    }

    // 1. Reward Filter
    if (rewardFilter === "zx") {
      if (!(stats.zxAmount > 0)) return false;
    } else if (rewardFilter === "crypto") {
      if (!(stats.hasCoinReward && stats.coinAmount > 0)) return false;
    }

    // 2. Status Filter
    if (statusFilter === "pending") {
      if (!stats.hasAnyPending) return false;
    } else if (statusFilter === "credited") {
      if (!stats.isFullyCredited && !stats.isZxCredited && !stats.isCryptoCredited) return false;
    }

    // 3. Search Filter
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    const uname = String(item.username || "").toLowerCase();
    const email = String(item.email || "").toLowerCase();
    const uid = String(item.userId || "");
    const hid = String(item.historyId || "").toLowerCase();
    const coinSym = String(stats.coinSymbol || "").toLowerCase();
    const txn = String(item.txnId || item.transactionId || "").toLowerCase();

    return (
      uname.includes(term) ||
      email.includes(term) ||
      uid.includes(term) ||
      hid.includes(term) ||
      coinSym.includes(term) ||
      txn.includes(term)
    );
  });

  // Calculate granular stats
  let totalZxPending = 0;
  let totalZxCredited = 0;
  const cryptoPendingTotals = {};
  const cryptoCreditedTotals = {};
  let pendingCount = 0;
  let creditedCount = 0;

  history.forEach((h) => {
    const s = getItemRewardStatus(h);
    if (s.isZxPending) {
      totalZxPending += s.zxAmount;
    }
    if (s.isZxCredited) {
      totalZxCredited += s.zxAmount;
    }
    if (s.isCryptoPending) {
      cryptoPendingTotals[s.coinSymbol] =
        (cryptoPendingTotals[s.coinSymbol] || 0) + s.coinAmount;
    }
    if (s.isCryptoCredited) {
      cryptoCreditedTotals[s.coinSymbol] =
        (cryptoCreditedTotals[s.coinSymbol] || 0) + s.coinAmount;
    }
    if (s.hasAnyPending) {
      pendingCount += 1;
    }
    if (s.isFullyCredited || s.isZxCredited || s.isCryptoCredited) {
      creditedCount += 1;
    }
  });

if (summaryData && summaryData.totalPending !== undefined && statusFilter !== "credited") {
      pendingCount = summaryData.totalPending;
      totalZxPending = summaryData.totalZxPending;
    }

  // Open Modal Helpers
  const openCryptoApproval = (item) => {
    setTxnIdInput(item.txnId || item.transactionId || "");
    setSelectedRoute("/home");
    setUrlInput("");
    setModalState({ type: "APPROVE_CRYPTO", target: item });
  };

  const openApproveZx = (item) => {
    setSelectedRoute("/home");
    setUrlInput("");
    setModalState({ type: "APPROVE_ZX", target: item });
  };

  const openRevertZx = (item) => {
    setModalState({ type: "REVERT_ZX", target: item });
  };

  const openRevertCrypto = (item) => {
    setModalState({ type: "REVERT_CRYPTO", target: item });
  };

  const openApproveAll = () => {
    if (totalRecords === 0) {
      toast.error("No records to approve");
      return;
    }
    setSelectedRoute("/home");
    setUrlInput("");
    setModalState({ type: "APPROVE_ALL" });
  };

  const openRevertAll = () => {
    if (totalRecords === 0) {
      toast.error("No records to revert");
      return;
    }
    setModalState({ type: "REVERT_ALL" });
  };

  const closeModal = () => {
    if (!modalLoading) {
      setModalState(null);
      setTxnIdInput("");
      setSelectedRoute("/home");
      setUrlInput("");
    }
  };

  // Open Custom Email Modal (Simultaneous Email + Push)
  const openEmailModal = (item) => {
    setSelectedWinnerForEmail(item);
    const eventName = selectedEvent?.eventName || "Zeta Rush 1.0";
    const rank = item.userRank ?? item.rank ?? 1;
    const userName = item.username || item.userName || "Winner";

    let rewardText = "";
    if (item.rewardZx > 0) {
      rewardText += `${Number(item.rewardZx).toLocaleString()} ZX`;
    }
    const coinAmt =
      item.rewardCoin?.coin ||
      item.rewardCoin?.coinAmount ||
      item.coinAmount ||
      item.rewardUsdt ||
      0;
    const coinSym =
      item.rewardCoin?.coinSymbol || item.coinSymbol || "USDT";
    if (coinAmt && Number(coinAmt) > 0) {
      rewardText += (rewardText ? " and " : "") + `${coinAmt} ${coinSym}`;
    }
    if (!rewardText) {
      rewardText = "your event prize";
    }

    if (coinAmt && Number(coinAmt) > 0) {
      setEmailSubject(`Congratulations! You Won a ${coinSym} Prize — ${eventName}`);
      setEmailMessage(
        `Hello ${userName},\n\nCongratulations! 🎉\n\nWe’re pleased to inform you that you have been selected as a ${coinSym} prize winner in ${eventName}.\n\n🏆 Rank: #${rank}\n💰 Prize: ${coinAmt} ${coinSym}\n\nTo process your prize, please reply to this email with the following details:\n\nBinance UID:\nRegistered Email:\n\nPlease make sure the information provided is accurate to avoid delays in prize processing.\n\nOnce your details are verified, our team will proceed with the ${coinSym} transfer according to the event prize process.\n\nThank you for participating in ${eventName} and for being an active member of the community.\n\nCongratulations once again, and thank you for being part of the Zeta Network journey.\n\nBest regards,\nZeta Network Team`
      );
    } else {
      setEmailSubject(`🎉 Congratulations on your reward in ${eventName}!`);
      setEmailMessage(
        `Dear ${userName},\n\nCongratulations on winning Rank #${rank} in "${eventName}"!\n\nYour reward of ${rewardText} has been approved.\n\nBest regards,\nZETA Network Team`
      );
    }
    setIsEmailModalOpen(true);
  };

  const closeEmailModal = () => {
    if (!sendingEmail) {
      setIsEmailModalOpen(false);
      setSelectedWinnerForEmail(null);
      setEmailSubject("");
      setEmailMessage("");
      setEmailRoute("/home");
      setEmailUrl("");
    }
  };

  const handleSendEmail = async (e) => {
    if (e) e.preventDefault();
    if (!emailSubject.trim() || !emailMessage.trim()) {
      toast.error("Please provide both Subject and Message.");
      return;
    }
    if (!selectedWinnerForEmail) return;

    setSendingEmail(true);
    try {
      const res = await sendWinnerEmail(selectedWinnerForEmail.historyId, {
        subject: emailSubject.trim(),
        message: emailMessage.trim(),
        route: emailRoute || "/home",
        url: emailUrl.trim() || "",
      });
      toast.success(
        res?.message ||
          `Email & Push Notification sent successfully to ${selectedWinnerForEmail.email || selectedWinnerForEmail.username}!`
      );
      setIsEmailModalOpen(false);
      setSelectedWinnerForEmail(null);
    } catch (err) {
      toast.error(err?.message || "Failed to send email and notification");
    } finally {
      setSendingEmail(false);
    }
  };

  // Open Custom Push Notification Modal
  const openPushModal = (item) => {
    setSelectedWinnerForPush(item);
    const eventName = selectedEvent?.eventName || "Zeta Rush 1.0";
    setPushTitle(`${eventName} Winner Notification`);
    setPushMessage(
      `Congratulations ${item.username || "Winner"}! Your reward of ${item.rewardZx || 0} ZX for "${eventName}" is ready.`
    );
    setPushRoute("/home");
    setPushUrl("");
    setIsPushModalOpen(true);
  };

  const closePushModal = () => {
    if (!sendingPush) {
      setIsPushModalOpen(false);
      setSelectedWinnerForPush(null);
      setPushTitle("");
      setPushMessage("");
      setPushRoute("/home");
      setPushUrl("");
    }
  };

  const handleSendPush = async (e) => {
    if (e) e.preventDefault();
    if (!pushTitle.trim() || !pushMessage.trim()) {
      toast.error("Please enter both Title and Message.");
      return;
    }
    if (!selectedWinnerForPush) return;

    setSendingPush(true);
    try {
      await sendWinnerPush(selectedWinnerForPush.historyId, {
        title: pushTitle.trim(),
        message: pushMessage.trim(),
        route: pushRoute || "/home",
        url: pushUrl.trim(),
      });
      toast.success(
        `Push notification sent successfully to ${selectedWinnerForPush.username || "user"}!`
      );
      setIsPushModalOpen(false);
      setSelectedWinnerForPush(null);
    } catch (err) {
      toast.error(err?.message || "Failed to send push notification");
    } finally {
      setSendingPush(false);
    }
  };

  // Table Row Multi-selection Handlers
  const toggleSelectAllPage = () => {
    const pageHistoryIds = filteredHistory
      .map((h) => h.historyId)
      .filter(Boolean);
    const allSelected =
      pageHistoryIds.length > 0 &&
      pageHistoryIds.every((id) => selectedHistoryIds.has(id));

    setSelectedHistoryIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        pageHistoryIds.forEach((id) => next.delete(id));
      } else {
        pageHistoryIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const toggleSelectWinner = (historyId) => {
    if (!historyId) return;
    setSelectedHistoryIds((prev) => {
      const next = new Set(prev);
      if (next.has(historyId)) {
        next.delete(historyId);
      } else {
        next.add(historyId);
      }
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedHistoryIds(new Set());
  };

  // Bulk Push Notification Handlers
  const openBulkPushModal = (target = null) => {
    const defaultAudience =
      target || (selectedHistoryIds.size > 0 ? "selected" : "all");
    setBulkTargetAudience(defaultAudience);

    const eventName = selectedEvent?.eventName || "Zeta Rush 1.0";
    setBulkPushTitle(`🎉 Reward Notification - ${eventName}`);
    setBulkPushMessage(
      `Congratulations {username}! Your reward of {reward} for Rank #{rank} in "${eventName}" has been confirmed. Open the app to view your rewards!`
    );
    setBulkPushRoute("/home");
    setBulkPushUrl("");
    setBulkPushProgress({
      current: 0,
      total: 0,
      percent: 0,
      successCount: 0,
      failCount: 0,
      currentName: "",
    });
    setIsBulkPushModalOpen(true);
  };

  const closeBulkPushModal = () => {
    if (!sendingBulkPush) {
      setIsBulkPushModalOpen(false);
      setBulkPushProgress({
        current: 0,
        total: 0,
        percent: 0,
        successCount: 0,
        failCount: 0,
        currentName: "",
      });
    }
  };

  const insertTagIntoMessage = (tag) => {
    setBulkPushMessage((prev) => `${prev ? prev + " " : ""}${tag}`);
  };

  const insertTagIntoTitle = (tag) => {
    setBulkPushTitle((prev) => `${prev ? prev + " " : ""}${tag}`);
  };

  const handleSendBulkPush = async (e) => {
    if (e) e.preventDefault();
    if (!bulkPushTitle.trim() || !bulkPushMessage.trim()) {
      toast.error("Please provide both Title and Message.");
      return;
    }

    setSendingBulkPush(true);
    setBulkPushProgress({
      current: 0,
      total: 0,
      percent: 0,
      successCount: 0,
      failCount: 0,
      currentName: "Preparing recipient list...",
    });

    try {
      let targetRecipients = [];

      if (bulkTargetAudience === "selected") {
        targetRecipients = history.filter((h) =>
          selectedHistoryIds.has(h.historyId)
        );
        if (targetRecipients.length === 0) {
          throw new Error("No winners are selected from the table.");
        }
      } else {
        const maxWinners = getMaxWinningRank(selectedEvent);
        // Reliably fetch all 500 winners across pages
        const targetStatus =
          bulkTargetAudience === "pending"
            ? "pending"
            : bulkTargetAudience === "credited"
            ? "credited"
            : "all";

        const fullList = await fetchAllWinnersForEvent(activeEventId, {
          maxCount: maxWinners, // Exactly 500
          rewardStatus: targetStatus,
        });

        let merged = mergeHistoryWithLocalApprovals(fullList);

        // Keep strictly only the 500 winners
        targetRecipients = merged.filter((item, idx) => {
          const rank = Number(item.userRank ?? item.rank ?? idx + 1);
          return rank <= maxWinners;
        });

        if (targetRecipients.length > maxWinners) {
          targetRecipients = targetRecipients.slice(0, maxWinners);
        }

        if (bulkTargetAudience === "pending") {
          targetRecipients = targetRecipients.filter((item) => {
            const st = getItemRewardStatus(item);
            return st.hasAnyPending;
          });
        } else if (bulkTargetAudience === "credited") {
          targetRecipients = targetRecipients.filter((item) => {
            const st = getItemRewardStatus(item);
            return st.isFullyCredited || st.isZxCredited || st.isCryptoCredited;
          });
        }
      }

      if (!targetRecipients || targetRecipients.length === 0) {
        toast.error("No winners found matching the chosen audience.");
        setSendingBulkPush(false);
        return;
      }

      setBulkPushProgress({
        current: 0,
        total: targetRecipients.length,
        percent: 0,
        successCount: 0,
        failCount: 0,
        currentName: "Dispatching notifications...",
      });

      const eventName = selectedEvent?.eventName || "Event";

      const result = await sendBulkWinnerPush({
        eventId: activeEventId,
        recipients: targetRecipients,
        payload: {
          title: bulkPushTitle.trim(),
          message: bulkPushMessage.trim(),
          route: bulkPushRoute || "/home",
          url: bulkPushUrl.trim(),
          eventName,
        },
        onProgress: (prog) => {
          setBulkPushProgress({
            current: prog.current,
            total: prog.total,
            percent:
              prog.percent ||
              Math.round((prog.current / prog.total) * 100),
            successCount: prog.successCount,
            failCount: prog.failCount,
            currentName: prog.winner?.username || "",
          });
        },
      });

      if (result.success) {
        toast.success(
          `Bulk push notification sent successfully to ${result.successCount} winner${
            result.successCount > 1 ? "s" : ""
          }!${result.failCount > 0 ? ` (${result.failCount} failed)` : ""}`
        );
        setTimeout(() => {
          setIsBulkPushModalOpen(false);
          setSendingBulkPush(false);
        }, 1200);
      } else {
        toast.error("Failed to complete bulk push notifications.");
        setSendingBulkPush(false);
      }
    } catch (err) {
      toast.error(err?.message || "Error sending bulk push notifications");
      setSendingBulkPush(false);
    }
  };

  // Sample Winner for previewing notification template dynamically
  const samplePreviewWinner =
    history.find((h) => selectedHistoryIds.has(h.historyId)) ||
    history[0] || {
      username: "subhashtc4188",
      userRank: 1,
      rewardZx: 100,
      rewardCoin: { coin: 10, coinSymbol: "USDT" },
      eventName: selectedEvent?.eventName || "Zeta Rush 1.0",
    };

  // Modal Execution Handler
  const handleExecuteModalAction = async (e) => {
    if (e) e.preventDefault();
    if (!modalState) return;

    setModalLoading(true);
    try {
      if (modalState.type === "APPROVE_CRYPTO") {
        const item = modalState.target;
        const txn = txnIdInput.trim();
        const extraPayload = {
          route: selectedRoute || "/home",
          url: urlInput.trim(),
        };
        if (txn) {
          extraPayload.txnId = txn;
          extraPayload.transactionId = txn;
        }
        await approveRewardStatus(item.historyId, "credited", extraPayload);

        setLocalRewardApproval(item.historyId, {
          cryptoStatus: "credited",
          txnId: txn || "TXN_CONFIRMED",
        });

        setHistory((prev) =>
          prev.map((h) =>
            h.historyId === item.historyId
              ? {
                  ...h,
                  cryptoStatus: "credited",
                  txnId: txn || h.txnId || "TXN_CONFIRMED",
                  transactionId: txn || h.transactionId || "TXN_CONFIRMED",
                  rewardCoin: {
                    ...(h.rewardCoin || {}),
                    status: "credited",
                    txnId: txn,
                  },
                }
              : h
          )
        );
        toast.success(`Crypto reward approved for ${item.username || "user"}!`);
      } else if (modalState.type === "APPROVE_ZX") {
        const item = modalState.target;
        const zxStats = getItemRewardStatus(item);
        if (!zxStats.isZxPending) {
          toast.error("This ZX reward is already credited or not eligible.");
          setModalLoading(false);
          return;
        }
        if (String(item.rewardStatus || "").toLowerCase() === "credited") {
          toast.error("This winner reward is already marked credited.");
          setModalLoading(false);
          return;
        }
        await approveRewardStatus(item.historyId, "credited", {
          route: selectedRoute || "/home",
          url: urlInput.trim(),
        });

        setLocalRewardApproval(item.historyId, {
          zxStatus: "credited",
        });

        setHistory((prev) =>
          prev.map((h) =>
            h.historyId === item.historyId
              ? {
                  ...h,
                  zxStatus: "credited",
                  rewardStatus:
                    h.rewardCoin &&
                    (Number(h.rewardCoin.coin) > 0 ||
                      Number(h.rewardCoin.coinAmount) > 0)
                      ? h.rewardStatus
                      : "credited",
                }
              : h
          )
        );
        toast.success(`ZX reward approved & credited for ${item.username || "user"}!`);
      } else if (modalState.type === "REVERT_ZX") {
        const item = modalState.target;
        await approveRewardStatus(item.historyId, "pending");

        setLocalRewardApproval(item.historyId, {
          zxStatus: "pending",
        });

        setHistory((prev) =>
          prev.map((h) =>
            h.historyId === item.historyId
              ? {
                  ...h,
                  zxStatus: "pending",
                  rewardStatus: "pending",
                }
              : h
          )
        );
        toast.success(`ZX reward reverted for ${item.username || "user"}!`);
      } else if (modalState.type === "REVERT_CRYPTO") {
        const item = modalState.target;
        await approveRewardStatus(item.historyId, "pending");

        setLocalRewardApproval(item.historyId, {
          cryptoStatus: "pending",
          txnId: "",
        });

        setHistory((prev) =>
          prev.map((h) =>
            h.historyId === item.historyId
              ? {
                  ...h,
                  cryptoStatus: "pending",
                  txnId: "",
                  transactionId: "",
                  rewardCoin: {
                    ...(h.rewardCoin || {}),
                    status: "pending",
                    txnId: "",
                  },
                }
              : h
          )
        );
        toast.success(`Crypto reward reverted for ${item.username || "user"}!`);
      } else if (modalState.type === "APPROVE_ALL") {
        try {
          const bulkRes = await approveAllEventRewards(activeEventId, {
            route: selectedRoute || "/home",
            url: urlInput.trim(),
          });
          const approved = bulkRes?.data?.approvedCount ?? bulkRes?.approvedCount;
          toast.success(
            bulkRes?.message ||
              `Approved ${approved != null ? approved : "pending"} pending ZX reward(s)`
          );
        } catch {
          // Fallback: only pending rows with ZX still owed
          const maxWinners = getMaxWinningRank(selectedEvent);
          const fullList = await fetchAllWinnersForEvent(activeEventId, {
            maxCount: maxWinners,
            rewardStatus: "pending",
          });

          const merged = mergeHistoryWithLocalApprovals(fullList);
          const allPending = merged.filter((h) => {
            const s = getItemRewardStatus(h);
            return (
              s.isZxPending &&
              s.zxAmount > 0 &&
              String(h.rewardStatus || "").toLowerCase() !== "credited"
            );
          });

          if (allPending.length === 0) {
            toast.error("No pending ZX rewards to approve.");
            setModalLoading(false);
            closeModal();
            return;
          }

          for (const item of allPending) {
            await approveRewardStatus(item.historyId, "credited", {
              route: selectedRoute || "/home",
              url: urlInput.trim(),
            });
          }
          toast.success(
            `Approved ${allPending.length} pending ZX reward(s) successfully!`
          );
        }
      } else if (modalState.type === "REVERT_ALL") {
        try {
          await revertAllEventRewards(activeEventId);
        } catch {
          // Fallback: batch sequential reverts for credited ZX items across all pages
          const maxWinners = getMaxWinningRank(selectedEvent);
          const fullList = await fetchAllWinnersForEvent(activeEventId, {
            maxCount: maxWinners,
            rewardStatus: "credited"
          });
          
          let merged = mergeHistoryWithLocalApprovals(fullList);
          const allCredited = merged.filter((h) => {
            const s = getItemRewardStatus(h);
            return s.isZxCredited;
          });
          
          for (const item of allCredited) {
            await approveRewardStatus(item.historyId, "pending");
          }
        }
        toast.success(`All credited ZX rewards reverted successfully!`);
      }

      closeModal();
      fetchHistory();
    } catch (err) {
      toast.error(err?.message || "Failed to process action");
    } finally {
      setModalLoading(false);
    }
  };

const isSearching = Boolean(debouncedSearch.trim() || searchTerm.trim());

// Rank Badge Display
const renderRankBadge = (rank, isSearchMode = isSearching) => {
  const numericRank = Number(rank);
  if (!isSearchMode) {
    if (numericRank === 1) {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-900 border border-amber-300 shadow-xs">
          <span>🥇</span> Rank 1
        </span>
      );
    }
    if (numericRank === 2) {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-slate-100 text-slate-800 border border-slate-300 shadow-xs">
          <span>🥈</span> Rank 2
        </span>
      );
    }
    if (numericRank === 3) {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-50 text-amber-950 border border-amber-200 shadow-xs">
          <span>🥉</span> Rank 3
        </span>
      );
    }
  }
  return (
    <span
      className="inline-flex items-center justify-center px-2 py-0.5 rounded-md text-xs font-semibold bg-gray-100 text-gray-700 font-mono"
      title={isSearchMode ? `Search result #${rank}` : `Rank #${rank}`}
    >
      #{rank ?? "—"}
    </span>
  );
};

return (
  <div className="flex bg-gray-100 min-h-screen">
    <Sidebar />
    <div className="flex-1 flex flex-col min-w-0">
      <Navbar />

      <div className="p-4 sm:p-8 space-y-5">
        {/* Breadcrumb Navigation */}
        <div className="flex items-center flex-wrap gap-1.5 text-xs font-medium text-gray-500">
          <Link to="/events" className="hover:text-black transition flex items-center gap-1 hover:underline">
            <FiCalendar size={13} /> Events
          </Link>
          <FiChevronRight size={12} className="text-gray-400 flex-shrink-0" />
          {activeEventId ? (
            <Link
              to={`/events/${activeEventId}`}
              className="hover:text-black transition max-w-xs truncate font-semibold hover:underline"
              title="View Event Details"
            >
              {selectedEvent?.eventName || activeEventId}
            </Link>
          ) : (
            <span className="text-gray-700 max-w-xs truncate font-semibold">
              {selectedEvent?.eventName || "Event"}
            </span>
          )}
          <FiChevronRight size={12} className="text-gray-400 flex-shrink-0" />
          <span className="text-black font-bold bg-[#ffffcc] px-2 py-0.5 rounded">
            Reward Coin Approval
          </span>
        </div>

        {/* Top Bar Header & Main Actions */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => {
                if (window.history.length > 1) {
                  navigate(-1);
                } else if (activeEventId) {
                  navigate(`/events/${activeEventId}`);
                } else {
                  navigate("/events");
                }
              }}
              className="p-2.5 bg-white hover:bg-gray-50 border border-gray-200 rounded-xl text-gray-700 hover:text-black transition shadow-xs cursor-pointer flex items-center justify-center"
              title="Back to Previous Page"
              aria-label="Back"
            >
              <FiArrowLeft size={18} />
            </button>
            <div>
              <h1 className="text-2xl font-black text-gray-900 tracking-tight">
                Reward Coin Approval
              </h1>
              <p className="text-xs text-gray-400 mt-0.5">
                Approve ZX tokens and Crypto payouts for leaderboard winners
              </p>
            </div>
          </div>

          {/* Action Bar */}
          <div className="flex flex-wrap items-center gap-2.5">
            {/* Event Switcher */}
            <div className="flex items-center gap-1.5 bg-white px-3 py-2 border border-gray-200 rounded-xl shadow-xs text-xs">
              <FiAward className="text-amber-500 flex-shrink-0" size={15} />
              <span className="text-gray-400 font-medium">Event:</span>
              <select
                value={activeEventId}
                onChange={handleEventChange}
                className="bg-transparent font-bold text-gray-800 focus:outline-none cursor-pointer max-w-[180px] sm:max-w-[220px] truncate"
              >
                {eventsList.map((evt) => (
                  <option key={evt.eventId || evt.id} value={evt.eventId || evt.id}>
                    {evt.eventName} ({evt.status})
                  </option>
                ))}
              </select>
            </div>

            {/* Refresh Button */}
            <button
              onClick={fetchHistory}
              disabled={loading}
              className="p-2.5 bg-white hover:bg-gray-50 border border-gray-200 rounded-xl text-gray-600 transition shadow-xs"
              title="Refresh Table"
            >
              <FiRefreshCw size={15} className={loading ? "animate-spin" : ""} />
            </button>

            <button
              type="button"
              onClick={handleSyncPrizesFromEvent}
              disabled={syncingPrizes || loading || !activeEventId}
              className="flex items-center gap-1.5 bg-amber-50 hover:bg-amber-100 text-amber-900 border border-amber-200 text-xs font-bold px-3.5 py-2.5 rounded-xl shadow-xs transition disabled:opacity-50"
              title="Write current prize tiers into pending winner records (required before Approve ZX credits real coins)"
            >
              <FiRefreshCw size={14} className={syncingPrizes ? "animate-spin" : ""} />
              <span>Sync prizes to winners</span>
            </button>

            {/* Event Notifications Link */}
            <button
              onClick={() =>
                navigate(
                  activeEventId
                    ? `/events/${activeEventId}/notifications`
                    : "/events/notifications"
                )
              }
              className="flex items-center gap-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 text-xs font-bold px-3.5 py-2.5 rounded-xl shadow-xs transition cursor-pointer"
              title="View & Dispatch Event Notifications"
            >
              <FiBell size={14} className="text-blue-600" />
              <span>Event Notifications</span>
            </button>

            {/* Bulk Push Notification Button */}
            <button
              onClick={() =>
                openBulkPushModal(
                  selectedHistoryIds.size > 0 ? "selected" : "all"
                )
              }
              disabled={loading || totalRecords === 0}
              className="flex items-center gap-1.5 bg-amber-400 hover:bg-amber-500 text-black text-xs font-black px-3.5 py-2.5 rounded-xl shadow-xs transition cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
              title="Send Bulk Push Notification to Event Winners"
            >
              <FiBell size={14} className="text-black" />
              <span>
                Bulk Push{" "}
                {selectedHistoryIds.size > 0
                  ? `(${selectedHistoryIds.size})`
                  : `(${totalRecords})`}
              </span>
            </button>

            {/* Revert All Coins Button (For ZX Coin Pool) */}
            <button
              onClick={openRevertAll}
              disabled={totalRecords === 0 || loading}
              className="flex items-center gap-1.5 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 text-xs font-bold px-3.5 py-2.5 rounded-xl shadow-xs transition disabled:opacity-40 disabled:cursor-not-allowed"
              title="Revert all credited ZX rewards back to pending"
            >
              <FiRotateCcw size={14} />
              <span>Revert All Coins</span>
            </button>

            {/* Approve All Coins Button (For ZX Coin Pool) */}
            <button
              onClick={openApproveAll}
              disabled={totalRecords === 0 || loading}
              className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-4 py-2.5 rounded-xl shadow-xs transition disabled:opacity-40 disabled:cursor-not-allowed"
              title="Approve all pending rewards"
            >
              <FiCheck size={15} />
              <span>Approve All Coins</span>
            </button>
          </div>
        </div>

        {/* Metric Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3.5">
          {/* Total Winners */}
          <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-xs flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Total Winners
              </p>
              <p className="text-xl font-black text-gray-800 mt-1">
                {totalRecords}
              </p>
            </div>
            <div className="w-10 h-10 rounded-xl bg-gray-50 text-gray-600 flex items-center justify-center font-bold">
              <FiAward size={18} />
            </div>
          </div>

          {/* Pending Rewards */}
          <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-xs flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-amber-600 uppercase tracking-wider flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                Pending Approvals
              </p>
              <p className="text-xl font-black text-amber-600 mt-1">
                {pendingCount}
              </p>
              <p className="text-[11px] text-amber-700 font-medium truncate max-w-[170px]">
                {totalZxPending > 0 ? `${totalZxPending.toLocaleString()} ZX` : ""}
                {Object.keys(cryptoPendingTotals).length > 0
                  ? ` + ${Object.entries(cryptoPendingTotals)
                    .map(([s, a]) => `${a} ${s}`)
                    .join(", ")}`
                  : ""}
              </p>
            </div>
            <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center font-bold">
              <FiClock size={18} />
            </div>
          </div>

          {/* Credited Rewards */}
          <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-xs flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                Credited Rewards
              </p>
              <p className="text-xl font-black text-emerald-600 mt-1">
                {creditedCount}
              </p>
              <p className="text-[11px] text-emerald-700 font-medium truncate max-w-[170px]">
                {totalZxCredited > 0 ? `${totalZxCredited.toLocaleString()} ZX` : ""}
                {Object.keys(cryptoCreditedTotals).length > 0
                  ? ` + ${Object.entries(cryptoCreditedTotals)
                    .map(([s, a]) => `${a} ${s}`)
                    .join(", ")}`
                  : ""}
              </p>
            </div>
            <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold">
              <FiCheckCircle size={18} />
            </div>
          </div>

          {/* Event Status */}
          <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-xs flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Event Status
              </p>
              <p className="text-sm font-bold text-gray-800 mt-1.5 capitalize">
                {selectedEvent?.status || "—"}
              </p>
              <p className="text-[11px] text-gray-400 truncate max-w-[150px]">
                {selectedEvent?.eventName || "Selected Event"}
              </p>
            </div>
            <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center font-bold">
              <FiCalendar size={18} />
            </div>
          </div>
        </div>

        {/* Search & Filter Bar */}
        <div className="bg-white rounded-xl p-3.5 border border-gray-100 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-3">
          {/* Search Input */}
          <div className="relative flex-1 max-w-md">
            <FiSearch
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
              size={15}
            />
            <input
              type="text"
              placeholder="Search by username, email, user ID, Txn ID..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full pl-9 pr-9 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-[#ffff00]"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5 rounded cursor-pointer"
                title="Clear search"
              >
                <FiX size={14} />
              </button>
            )}
          </div>

          {/* Filter Pills */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Reward Type Filter */}
            <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => {
                  setRewardFilter("all");
                  setPage(1);
                }}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition ${rewardFilter === "all"
                    ? "bg-white text-black shadow-xs font-bold"
                    : "text-gray-500 hover:text-gray-900"
                  }`}
              >
                All Rewards ({totalRecords})
              </button>
              <button
                onClick={() => {
                  setRewardFilter("crypto");
                  setPage(1);
                }}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition flex items-center gap-1 ${rewardFilter === "crypto"
                    ? "bg-emerald-600 text-white shadow-xs font-bold"
                    : "text-gray-600 hover:text-emerald-800"
                  }`}
              >
                <span>🪙</span>
                <span>Crypto Approvals ({cryptoList.length})</span>
              </button>
              <button
                onClick={() => {
                  setRewardFilter("zx");
                  setPage(1);
                }}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition flex items-center gap-1 ${rewardFilter === "zx"
                    ? "bg-purple-600 text-white shadow-xs font-bold"
                    : "text-gray-600 hover:text-purple-800"
                  }`}
              >
                <FiZap size={12} />
                <span>ZX Rewards ({zxList.length})</span>
              </button>
            </div>

            {/* Status Filter */}
            <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg">
              {STATUS_FILTERS.map((tab) => {
                const isActive = statusFilter === tab.value;
                return (
                  <button
                    key={tab.value}
                    onClick={() => {
                      setStatusFilter(tab.value);
                      setPage(1);
                    }}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold transition flex items-center gap-1.5 ${isActive
                        ? "bg-white text-black shadow-xs font-bold"
                        : "text-gray-500 hover:text-gray-900"
                      }`}
                  >
                    {tab.dot && <span className={`w-1.5 h-1.5 rounded-full ${tab.dot}`} />}
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Selected Winners Active Action Bar */}
        {selectedHistoryIds.size > 0 && (
          <div className="bg-amber-400 text-black px-4 py-3 rounded-xl shadow-md flex flex-wrap items-center justify-between gap-3 animate-in fade-in duration-150">
            <div className="flex items-center gap-2 text-xs font-black">
              <FiCheckCircle size={18} />
              <span>
                {selectedHistoryIds.size} winner
                {selectedHistoryIds.size > 1 ? "s" : ""} selected for bulk action
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => openBulkPushModal("selected")}
                className="bg-black hover:bg-gray-900 text-white px-3.5 py-1.5 rounded-lg flex items-center gap-1.5 text-xs font-bold transition shadow-xs cursor-pointer"
              >
                <FiBell size={13} />
                <span>Send Bulk Push ({selectedHistoryIds.size})</span>
              </button>
              <button
                type="button"
                onClick={clearSelection}
                className="bg-amber-500 hover:bg-amber-600 text-black px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer"
              >
                Deselect All
              </button>
            </div>
          </div>
        )}

        {/* Ranking Rule Note */}
        <div className="bg-amber-50/70 border border-amber-200/80 rounded-xl px-4 py-2.5 flex items-center justify-between text-xs text-amber-900 shadow-xs">
          <div className="flex items-center gap-2">
            <span className="text-base">📊</span>
            <span>
              <strong>Leaderboard Ranking Basis:</strong> Users are ranked based on <strong>Total Coins Earned from Daily Check-ins (🪙 Check-in Coins)</strong> (Tie-breaker: <strong>Earliest Completion Date/Time</strong>).
            </span>
          </div>
        </div>

        {/* Unified Winners Table */}
        {loading && history.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 p-16 flex flex-col items-center justify-center gap-3">
            <div className="w-8 h-8 border-3 border-[#ffff00] border-t-transparent rounded-full animate-spin" />
            <p className="text-xs text-gray-400">Loading winners and reward records...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-6 text-sm flex flex-col items-center gap-3 text-center">
            <FiAlertCircle size={24} className="text-red-500" />
            <p className="font-semibold">{error}</p>
            <button
              onClick={fetchHistory}
              className="px-4 py-2 bg-red-600 text-white rounded-lg text-xs font-semibold hover:bg-red-700 transition"
            >
              Retry
            </button>
          </div>
        ) : filteredHistory.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 p-12 text-center space-y-3 shadow-xs">
            <div className="w-12 h-12 rounded-full bg-yellow-50 text-[#999900] flex items-center justify-center mx-auto text-xl font-bold">
              🏆
            </div>
            <h3 className="text-sm font-bold text-gray-800">No Winner Records Found</h3>
            <p className="text-xs text-gray-400 max-w-sm mx-auto">
              {statusFilter !== "all" || searchTerm || rewardFilter !== "all"
                ? "No records match the active filter criteria."
                : "No winner history is available for this event yet."}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200/80 shadow-xs overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm border-collapse">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-[11px] text-gray-500 uppercase font-bold tracking-wider">
                    <th className="w-10 px-4 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={
                          filteredHistory.length > 0 &&
                          filteredHistory.every((h) =>
                            selectedHistoryIds.has(h.historyId)
                          )
                        }
                        onChange={toggleSelectAllPage}
                        className="w-4 h-4 rounded text-amber-600 focus:ring-amber-500 border-gray-300 cursor-pointer accent-amber-500"
                        title="Select/Deselect all on this page"
                      />
                    </th>
                    <th className="px-5 py-3 w-20">Rank</th>
                    <th className="px-5 py-3 min-w-[200px]">User Info</th>
                    <th className="px-5 py-3 text-center min-w-[170px]">
                      <span title="Total Event Points earned by user">
                        Event Points (Score)
                      </span>
                    </th>
                    <th className="px-5 py-3 min-w-[240px]">Rewards (ZX &amp; Crypto)</th>
                    <th className="px-5 py-3 whitespace-nowrap">Completion Date</th>
                    <th className="px-5 py-3 whitespace-nowrap">Status</th>
                    <th className="px-5 py-3 text-center w-48 whitespace-nowrap">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredHistory.map((item, idx) => {
                    const fallbackRank = (page - 1) * limit + idx + 1;
                    const matchedParticipant = findMatchingParticipant(
                      item,
                      participantsMap,
                      fallbackRank
                    );
                    const { earnedCoins, checkinCount } = calculateUserCoins(
                      item,
                      matchedParticipant,
                      selectedEvent
                    );
                    const stats = getItemRewardStatus(item);
                    const txnIdentifier = item.txnId || item.transactionId;
                    const hasRewardAmount =
                      stats.zxAmount > 0 ||
                      (stats.hasCoinReward && stats.coinAmount > 0);

                    return (
                      <tr
                        key={item.historyId || idx}
                        className={`transition ${
                          selectedHistoryIds.has(item.historyId)
                            ? "bg-amber-50/50 hover:bg-amber-50/80"
                            : "hover:bg-yellow-50/20"
                        }`}
                      >
                        {/* Row Selection Checkbox */}
                        <td className="w-10 px-4 py-3.5 text-center">
                          <input
                            type="checkbox"
                            checked={selectedHistoryIds.has(item.historyId)}
                            onChange={() => toggleSelectWinner(item.historyId)}
                            className="w-4 h-4 rounded text-amber-600 focus:ring-amber-500 border-gray-300 cursor-pointer accent-amber-500"
                            title={`Select ${item.username || `#${item.userId}`}`}
                          />
                        </td>

                        {/* Rank */}
                        <td className="px-5 py-3.5 whitespace-nowrap">
                          {renderRankBadge(item.userRank ?? (page - 1) * limit + idx + 1, isSearching)}
                        </td>

                        {/* User Info */}
                        <td className="px-5 py-3.5">
                          <Link
                            to={`/user/${item.userId}`}
                            className="flex items-center gap-2.5 group hover:opacity-95 transition"
                            title={`View User Details for ${item.username || "User"} (#${item.userId})`}
                          >
                            <div className="w-8 h-8 rounded-full bg-yellow-100 group-hover:bg-[#ffff00] text-black flex items-center justify-center font-bold text-xs flex-shrink-0 transition">
                              {item.username ? item.username.charAt(0).toUpperCase() : <FiUser />}
                            </div>
                            <div className="min-w-0">
                              <span className="font-bold text-gray-900 group-hover:text-blue-600 block truncate text-xs transition">
                                {item.username || "Anonymous"}
                              </span>
                              <div className="flex items-center gap-2 mt-0.5 text-[11px] text-gray-400">
                                <span className="truncate max-w-[160px]">{item.email || "No email"}</span>
                                <span className="font-mono text-gray-500 bg-gray-100 group-hover:bg-blue-50 group-hover:text-blue-600 px-1 rounded text-[10px] transition">
                                  #{item.userId}
                                </span>
                              </div>
                            </div>
                          </Link>
                        </td>

                        {/* Event Points / Rank Basis */}
                        <td className="px-5 py-3.5 text-center whitespace-nowrap">
                          <div className="inline-flex flex-col items-center gap-0.5">
                            <span className="inline-flex items-center gap-1 font-black text-xs bg-amber-50 text-amber-900 border border-amber-200 px-2.5 py-1 rounded-lg shadow-xs">
                              <span>🎯</span> {Number(item.eventPoints || item.points || earnedCoins || 0).toLocaleString()} PTS
                            </span>
                            {checkinCount > 0 && (
                              <span className="text-[10px] text-gray-400 font-medium">
                                ({checkinCount} check-in{checkinCount > 1 ? "s" : ""})
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Rewards: ZX + Crypto Together */}
                        <td className="px-5 py-3.5">
                          <div className="flex flex-wrap items-center gap-1.5">
                            {/* ZX Badge */}
                            {stats.zxAmount > 0 && (
                              <span
                                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-bold border ${stats.isZxCredited
                                    ? "bg-purple-100 text-purple-800 border-purple-300"
                                    : "bg-purple-50 text-purple-700 border-purple-200"
                                  }`}
                                title={stats.isZxCredited ? "ZX Coins Credited to User Balance" : "ZX Coins Pending Approval"}
                              >
                                <FiZap className="text-purple-600 fill-purple-600" size={12} />
                                <span>{stats.zxAmount.toLocaleString()} ZX</span>
                                {stats.isZxCredited && <span className="text-[10px] text-purple-700 ml-0.5 font-black">✓</span>}
                              </span>
                            )}

                            {/* Crypto Badge */}
                            {stats.hasCoinReward && stats.coinAmount > 0 && (
                              <span
                                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-bold border ${stats.isCryptoCredited
                                    ? "bg-emerald-100 text-emerald-900 border-emerald-300"
                                    : "bg-emerald-50 text-emerald-800 border-emerald-200"
                                  }`}
                                title={stats.isCryptoCredited ? "Crypto Payout Completed" : "Crypto Payout Pending Approval"}
                              >
                                <span>🪙</span>
                                <span>{stats.coinAmount.toLocaleString()} {stats.coinSymbol}</span>
                                {stats.isCryptoCredited && <span className="text-[10px] text-emerald-800 ml-0.5 font-black">✓</span>}
                              </span>
                            )}

                            {stats.zxAmount === 0 && !stats.hasCoinReward && (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </div>

                          {/* Txn ID if available */}
                          {txnIdentifier && (
                            <div
                              className="flex items-center gap-1 text-[10px] text-gray-500 font-mono bg-gray-50 border border-gray-200 px-1.5 py-0.5 rounded max-w-fit mt-1 cursor-pointer hover:bg-gray-100"
                              onClick={() => {
                                navigator.clipboard.writeText(txnIdentifier);
                                toast.success("Txn ID copied!");
                              }}
                              title="Click to copy Txn ID"
                            >
                              <FiHash size={10} className="text-gray-400" />
                              <span className="truncate max-w-[120px]">{txnIdentifier}</span>
                            </div>
                          )}
                        </td>

                        {/* Date */}
                        <td className="px-5 py-3.5 whitespace-nowrap text-xs text-gray-600">
                          {formatUtcDateTime(item.completionDate)}
                        </td>

                        {/* Status */}
                        <td className="px-5 py-3.5 whitespace-nowrap">
                          {stats.isFullyCredited ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                              Credited
                            </span>
                          ) : stats.isZxCredited && stats.isCryptoPending ? (
                            <span
                              className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-purple-50 text-purple-700 border border-purple-200"
                              title="ZX Credited, Crypto Pending Approval"
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-purple-500" />
                              ZX Credited
                            </span>
                          ) : stats.isCryptoCredited && stats.isZxPending ? (
                            <span
                              className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-teal-50 text-teal-700 border border-teal-200"
                              title="Crypto Paid, ZX Pending Approval"
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-teal-500" />
                              Crypto Paid
                            </span>
                          ) : !hasRewardAmount ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-50 text-gray-500 border border-gray-200">
                              No prize tier
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200">
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                              Pending
                            </span>
                          )}
                        </td>

                        {/* Actions */}
                        <td className="px-5 py-3.5 whitespace-nowrap text-center">
                          <div className="flex flex-wrap items-center justify-center gap-1.5 min-w-[200px]">
                            {/* 1. ZX Approval Button */}
                            {stats.zxAmount > 0 && (
                              stats.isZxPending ? (
                                <button
                                  type="button"
                                  onClick={() => openApproveZx(item)}
                                  className="inline-flex items-center justify-center gap-1 bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs px-2.5 py-1.5 rounded-lg shadow-xs transition"
                                  title="Approve & Credit ZX Coins to User"
                                >
                                  <FiZap size={12} />
                                  <span>Approve ZX</span>
                                </button>
                              ) : (
                                <span
                                  className="inline-flex items-center gap-1 bg-purple-50 text-purple-700 border border-purple-200 font-bold text-xs px-2 py-1 rounded-lg"
                                  title="ZX Coins Credited"
                                >
                                  <FiCheck size={12} />
                                  <span>ZX Credited</span>
                                </span>
                              )
                            )}

                            {/* 2. Crypto Approval Button */}
                            {stats.hasCoinReward && stats.coinAmount > 0 && (
                              stats.isCryptoPending ? (
                                <button
                                  type="button"
                                  onClick={() => openCryptoApproval(item)}
                                  className="inline-flex items-center justify-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs px-2.5 py-1.5 rounded-lg shadow-xs transition"
                                  title="Approve Crypto Reward (Enter Txn ID)"
                                >
                                  <span>🪙</span>
                                  <span>Crypto Approval</span>
                                </button>
                              ) : (
                                <span
                                  className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-800 border border-emerald-200 font-bold text-xs px-2 py-1 rounded-lg"
                                  title={`Crypto payout recorded${txnIdentifier ? `: ${txnIdentifier}` : ""}`}
                                >
                                  <span>🪙</span>
                                  <span>Crypto Paid</span>
                                </span>
                              )
                            )}

                            {/* 3. Send Custom Email Action (Outside for USDT Winners) */}
                            {stats.hasCoinReward && stats.coinAmount > 0 && (
                              <button
                                type="button"
                                onClick={() => openEmailModal(item)}
                                className="inline-flex items-center justify-center gap-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 font-bold text-xs px-2.5 py-1.5 rounded-lg shadow-xs transition cursor-pointer"
                                title={`Send Email & Push Notification (Binance ID / Reward) to ${item.email || item.username}`}
                              >
                                <FiMail size={12} />
                                <span>Send Mail / Binance ID</span>
                              </button>
                            )}

                            {/* 4. Three-Dot Menu (Send Mail, Send Push, Revert ZX, Revert Crypto) */}
                            <div className="relative" onClick={(e) => e.stopPropagation()}>
                              <button
                                type="button"
                                onClick={() => setOpenDropdownId(openDropdownId === item.historyId ? null : item.historyId)}
                                className="p-1.5 text-gray-500 hover:text-black hover:bg-gray-100 rounded-lg transition flex items-center justify-center cursor-pointer border border-transparent hover:border-gray-200"
                                title="More Options"
                              >
                                <FiMoreVertical size={16} />
                              </button>
                              
                              {openDropdownId === item.historyId && (
                                <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-gray-200 shadow-xl rounded-xl flex flex-col z-50 py-1 overflow-hidden animate-in fade-in zoom-in duration-100">
                                  
                                  {/* Send Push */}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setOpenDropdownId(null);
                                      openPushModal(item);
                                    }}
                                    className="w-full px-4 py-2 text-left text-xs font-semibold text-gray-700 hover:bg-gray-50 hover:text-black transition flex items-center gap-2 cursor-pointer"
                                  >
                                    <FiBell size={13} className="text-amber-500" />
                                    <span>Send Push</span>
                                  </button>

                                  {/* Send Mail (Inside for non-USDT Winners) */}
                                  {!(stats.hasCoinReward && stats.coinAmount > 0) && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setOpenDropdownId(null);
                                        openEmailModal(item);
                                      }}
                                      className="w-full px-4 py-2 text-left text-xs font-semibold text-blue-600 hover:bg-blue-50 transition flex items-center gap-2 cursor-pointer"
                                    >
                                      <FiMail size={13} />
                                      <span>Send Mail</span>
                                    </button>
                                  )}

                                  {/* Revert ZX */}
                                  {stats.zxAmount > 0 && !stats.isZxPending && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setOpenDropdownId(null);
                                        openRevertZx(item);
                                      }}
                                      className="w-full px-4 py-2 text-left text-xs font-semibold text-rose-600 hover:bg-rose-50 transition flex items-center gap-2 cursor-pointer"
                                    >
                                      <FiRotateCcw size={13} />
                                      <span>Revert ZX</span>
                                    </button>
                                  )}

                                  {/* Revert Crypto */}
                                  {stats.hasCoinReward && stats.coinAmount > 0 && !stats.isCryptoPending && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setOpenDropdownId(null);
                                        openRevertCrypto(item);
                                      }}
                                      className="w-full px-4 py-2 text-left text-xs font-semibold text-rose-600 hover:bg-rose-50 transition flex items-center gap-2 cursor-pointer"
                                    >
                                      <FiRotateCcw size={13} />
                                      <span>Revert Crypto</span>
                                    </button>
                                  )}

                                </div>
                              )}
                            </div>

                            {/* 5. Fallback if no specific rewards */}
                            {stats.zxAmount === 0 && (!stats.hasCoinReward || stats.coinAmount === 0) && (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2">
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span>Show</span>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
                className="px-2 py-1 border border-gray-200 rounded-md bg-white text-xs"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>per page (Total {totalRecords})</span>
            </div>

            <div className="flex items-center gap-1">
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-2.5 py-1 text-xs font-semibold border rounded-md bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                &laquo; Prev
              </button>
              <span className="px-3 py-1 text-xs font-bold bg-[#ffff00] text-black rounded-md">
                {page} / {totalPages}
              </span>
              <button
                disabled={page === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="px-2.5 py-1 text-xs font-semibold border rounded-md bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next &raquo;
              </button>
            </div>
          </div>
        )}
      </div>
    </div>

    {/* Confirmation Modals */}
    {modalState && (
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center z-50 p-4"
        onClick={closeModal}
      >
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-150"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
            <div className="flex items-center gap-2.5">
              {modalState.type === "APPROVE_CRYPTO" ? (
                <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-800 flex items-center justify-center font-bold text-base">
                  🪙
                </div>
              ) : modalState.type.includes("APPROVE") ? (
                <div className="w-8 h-8 rounded-lg bg-purple-100 text-purple-800 flex items-center justify-center font-bold">
                  <FiCheck size={16} />
                </div>
              ) : (
                <div className="w-8 h-8 rounded-lg bg-rose-100 text-rose-700 flex items-center justify-center font-bold">
                  <FiAlertTriangle size={16} />
                </div>
              )}
              <h3 className="text-base font-bold text-gray-900">
                {modalState.type === "APPROVE_CRYPTO" && "Crypto Reward Approval"}
                {modalState.type === "APPROVE_ZX" && "Approve ZX Coin Reward"}
                {modalState.type === "REVERT_ZX" && "Revert ZX Coin Reward"}
                {modalState.type === "REVERT_CRYPTO" && "Revert Crypto Payout"}
                {modalState.type === "REVERT_SINGLE" && "Revert Winner Reward"}
                {modalState.type === "APPROVE_ALL" && "Approve All Pending ZX Rewards"}
                {modalState.type === "REVERT_ALL" && "Revert All Credited ZX Rewards"}
              </h3>
            </div>
            <button
              onClick={closeModal}
              disabled={modalLoading}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-lg"
            >
              <FiX size={18} />
            </button>
          </div>

          {/* Modal Content Form */}
          <form onSubmit={handleExecuteModalAction} className="p-6 space-y-4">
            {/* APPROVE_CRYPTO Content */}
            {modalState.type === "APPROVE_CRYPTO" && (
              <>
                <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3.5 space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-emerald-800">Winner:</span>
                    <span className="font-bold text-gray-900">
                      {modalState.target.username} (#{modalState.target.userId})
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-emerald-800">Rank:</span>
                    <span className="font-bold text-gray-900">
                      Rank #{modalState.target.userRank}
                    </span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-emerald-200">
                    <span className="text-emerald-900 font-bold">Crypto Prize:</span>
                    <span className="text-xs font-black text-emerald-800 bg-white px-2.5 py-0.5 rounded border border-emerald-200 shadow-xs">
                      🪙 {modalState.target.rewardCoin?.coin ?? modalState.target.rewardCoin?.coinAmount}{" "}
                      {modalState.target.rewardCoin?.coinSymbol || "USDT"}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    Transaction ID / Txn Hash <span className="text-gray-400 font-normal">(Optional / On-chain Hash)</span>
                  </label>
                  <div className="relative">
                    <FiHash className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                    <input
                      type="text"
                      placeholder="e.g. 0x8f2d... or TXN_98721389"
                      value={txnIdInput}
                      onChange={(e) => setTxnIdInput(e.target.value)}
                      className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-xs font-mono focus:outline-none focus:ring-2 focus:ring-[#ffff00] bg-gray-50 focus:bg-white transition"
                      autoFocus
                    />
                  </div>
                  <p className="text-[11px] text-gray-400 mt-1 leading-relaxed">
                    Enter the on-chain payout transaction hash to confirm this individual crypto transfer.
                  </p>
                </div>

                {/* Push Notification Route Selector */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    📱 Push Notification Target Route (App Screen)
                  </label>
                  <select
                    value={selectedRoute}
                    onChange={(e) => setSelectedRoute(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs font-medium focus:outline-none focus:ring-2 focus:ring-[#ffff00] bg-gray-50 focus:bg-white transition cursor-pointer"
                  >
                    {APP_ROUTES.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label} ({r.value})
                      </option>
                    ))}
                  </select>
                  <p className="text-[11px] text-gray-400 mt-1">
                    User will be redirected to this screen when tapping the push notification.
                  </p>
                </div>

                {/* External URL Input */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    🌐 External URL <span className="text-gray-400 font-normal">(Optional Web Link)</span>
                  </label>
                  <input
                    type="text"
                    placeholder="https://minezeta.com"
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs font-mono focus:outline-none focus:ring-2 focus:ring-[#ffff00] bg-gray-50 focus:bg-white transition"
                  />
                </div>
              </>
            )}

            {/* APPROVE_ZX Content */}
            {modalState.type === "APPROVE_ZX" && (
              <>
                <div className="bg-purple-50 border border-purple-100 rounded-xl p-3.5 space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-purple-700">Winner:</span>
                    <span className="font-bold text-gray-900">
                      {modalState.target.username} (#{modalState.target.userId})
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-purple-700">Rank:</span>
                    <span className="font-bold text-gray-900">
                      Rank #{modalState.target.userRank}
                    </span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-purple-200">
                    <span className="text-purple-900 font-bold">ZX Reward:</span>
                    <span className="font-bold text-purple-700 bg-white px-2 py-0.5 rounded border border-purple-200">
                      ⚡ {Number(modalState.target.rewardZx).toLocaleString()} ZX
                    </span>
                  </div>
                </div>

                {/* Push Notification Route Selector */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    📱 Push Notification Target Route (App Screen)
                  </label>
                  <select
                    value={selectedRoute}
                    onChange={(e) => setSelectedRoute(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs font-medium focus:outline-none focus:ring-2 focus:ring-purple-400 bg-gray-50 focus:bg-white transition cursor-pointer"
                  >
                    {APP_ROUTES.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label} ({r.value})
                      </option>
                    ))}
                  </select>
                  <p className="text-[11px] text-gray-400 mt-1">
                    User will be redirected to this screen when tapping the push notification.
                  </p>
                </div>

                {/* External URL Input */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    🌐 External URL <span className="text-gray-400 font-normal">(Optional Web Link)</span>
                  </label>
                  <input
                    type="text"
                    placeholder="https://minezeta.com"
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs font-mono focus:outline-none focus:ring-2 focus:ring-purple-400 bg-gray-50 focus:bg-white transition"
                  />
                </div>

                <p className="text-xs text-gray-600">
                  Are you sure you want to approve &amp; credit {Number(modalState.target.rewardZx).toLocaleString()} ZX coins directly to user account balance?
                </p>
              </>
            )}

            {/* REVERT_ZX Content */}
            {modalState.type === "REVERT_ZX" && (
              <>
                <div className="bg-rose-50 border border-rose-100 rounded-xl p-3.5 space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-rose-700">Winner:</span>
                    <span className="font-bold text-rose-900">
                      {modalState.target.username} (#{modalState.target.userId})
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-rose-700">Rank:</span>
                    <span className="font-bold text-rose-900">
                      Rank #{modalState.target.userRank}
                    </span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-rose-200">
                    <span className="text-rose-900 font-bold">ZX To Revert:</span>
                    <span className="font-bold text-rose-700 bg-white px-2 py-0.5 rounded border border-rose-200">
                      ⚡ {Number(modalState.target.rewardZx).toLocaleString()} ZX
                    </span>
                  </div>
                </div>

                <p className="text-xs text-gray-600 leading-relaxed">
                  ⚠️ <strong>Warning:</strong> Reverting ZX reward will deduct {Number(modalState.target.rewardZx).toLocaleString()} ZX coins from user balance and reset ZX status back to <strong>Pending</strong>. Crypto reward will NOT be affected.
                </p>
              </>
            )}

            {/* REVERT_CRYPTO Content */}
            {modalState.type === "REVERT_CRYPTO" && (
              <>
                <div className="bg-rose-50 border border-rose-100 rounded-xl p-3.5 space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-rose-700">Winner:</span>
                    <span className="font-bold text-rose-900">
                      {modalState.target.username} (#{modalState.target.userId})
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-rose-700">Rank:</span>
                    <span className="font-bold text-rose-900">
                      Rank #{modalState.target.userRank}
                    </span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-rose-200">
                    <span className="text-rose-900 font-bold">Crypto To Revert:</span>
                    <span className="font-bold text-rose-700 bg-white px-2 py-0.5 rounded border border-rose-200">
                      🪙 {modalState.target.rewardCoin?.coin ?? modalState.target.rewardCoin?.coinAmount}{" "}
                      {modalState.target.rewardCoin?.coinSymbol || "USDT"}
                    </span>
                  </div>
                </div>

                <p className="text-xs text-gray-600 leading-relaxed">
                  ⚠️ <strong>Warning:</strong> Reverting Crypto reward will clear the transaction hash and reset Crypto payout status back to <strong>Pending Approval</strong>. ZX reward will NOT be affected.
                </p>
              </>
            )}

            {/* REVERT_SINGLE Content */}
            {modalState.type === "REVERT_SINGLE" && (
              <>
                <div className="bg-rose-50 border border-rose-100 rounded-xl p-3.5 space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-rose-700">Winner:</span>
                    <span className="font-bold text-rose-900">
                      {modalState.target.username} (#{modalState.target.userId})
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-rose-700">Rank:</span>
                    <span className="font-bold text-rose-900">
                      Rank #{modalState.target.userRank}
                    </span>
                  </div>
                </div>

                <p className="text-xs text-gray-600 leading-relaxed">
                  ⚠️ <strong>Warning:</strong> Reverting this reward will deduct the credited coins from user balance and reset status back to <strong>Pending</strong>.
                </p>
              </>
            )}

            {/* APPROVE_ALL Content */}
            {modalState.type === "APPROVE_ALL" && (
              <>
                <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3.5 space-y-2 text-xs text-emerald-900">
                  <div className="flex justify-between font-medium">
                    <span>Event:</span>
                    <span className="font-bold">{selectedEvent?.eventName || activeEventId}</span>
                  </div>
                  <div className="flex justify-between font-medium">
                    <span>Pending Winners:</span>
                    <span className="font-bold text-emerald-700">{pendingCount} winners</span>
                  </div>
                  <div className="flex justify-between font-medium">
                    <span>Total Coins to Approve:</span>
                    <span className="font-bold text-emerald-700">{Number(totalZxPending).toLocaleString()} ZX</span>
                  </div>
                </div>

                {/* Push Notification Route Selector */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    📱 Push Notification Target Route (App Screen)
                  </label>
                  <select
                    value={selectedRoute}
                    onChange={(e) => setSelectedRoute(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs font-medium focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-gray-50 focus:bg-white transition cursor-pointer"
                  >
                    {APP_ROUTES.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label} ({r.value})
                      </option>
                    ))}
                  </select>
                  <p className="text-[11px] text-gray-400 mt-1">
                    Users will be redirected to this screen when tapping the bulk reward push notifications.
                  </p>
                </div>

                {/* External URL Input */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    🌐 External URL <span className="text-gray-400 font-normal">(Optional Web Link)</span>
                  </label>
                  <input
                    type="text"
                    placeholder="https://minezeta.com"
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs font-mono focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-gray-50 focus:bg-white transition"
                  />
                </div>

                <p className="text-xs text-gray-600">
                  Are you sure you want to approve &amp; credit rewards for all <strong>{pendingCount}</strong> pending winners in this event?
                </p>
              </>
            )}

            {/* REVERT_ALL Content */}
            {modalState.type === "REVERT_ALL" && (
              <>
                <div className="bg-rose-50 border border-rose-100 rounded-xl p-3.5 space-y-2 text-xs text-rose-900">
                  <div className="flex justify-between font-medium">
                    <span>Event:</span>
                    <span className="font-bold">{selectedEvent?.eventName || activeEventId}</span>
                  </div>
                  <div className="flex justify-between font-medium">
                    <span>Credited Winners:</span>
                    <span className="font-bold text-rose-700">{creditedCount} winners</span>
                  </div>
                </div>

                <p className="text-xs text-gray-600 leading-relaxed">
                  ⚠️ <strong>Warning:</strong> Are you sure you want to revert ALL credited rewards for this event? All credited coins will be rolled back from users&apos; accounts and returned to <strong>Pending</strong>.
                </p>
              </>
            )}

            {/* Footer Buttons */}
            <div className="pt-3 border-t border-gray-100 flex items-center justify-end gap-2.5">
              <button
                type="button"
                onClick={closeModal}
                disabled={modalLoading}
                className="px-4 py-2 text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={modalLoading}
                className={`px-5 py-2 text-xs font-bold text-white rounded-lg transition flex items-center gap-1.5 shadow-xs disabled:opacity-50 ${modalState.type.includes("REVERT")
                    ? "bg-rose-600 hover:bg-rose-700"
                    : "bg-emerald-600 hover:bg-emerald-700"
                  }`}
              >
                {modalLoading ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Processing...</span>
                  </>
                ) : modalState.type === "APPROVE_CRYPTO" ? (
                  <>
                    <FiCheck size={14} />
                    <span>Confirm &amp; Approve Crypto</span>
                  </>
                ) : modalState.type === "APPROVE_ZX" ? (
                  <>
                    <FiCheck size={14} />
                    <span>Confirm ZX Approval</span>
                  </>
                ) : modalState.type === "REVERT_SINGLE" ? (
                  <>
                    <FiRotateCcw size={13} />
                    <span>Confirm Revert</span>
                  </>
                ) : modalState.type === "APPROVE_ALL" ? (
                  <>
                    <FiCheck size={14} />
                    <span>Approve All ({pendingCount})</span>
                  </>
                ) : (
                  <>
                    <FiRotateCcw size={13} />
                    <span>Revert All ({creditedCount})</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    )}

    {/* Send Custom Winner Email & Push Modal */}
    {isEmailModalOpen && selectedWinnerForEmail && (
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center z-50 p-4"
        onClick={closeEmailModal}
      >
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden animate-in fade-in zoom-in-95 duration-150"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-indigo-100 text-indigo-800 flex items-center justify-center font-bold">
                <FiSend size={16} />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">
                  Send Winner Email &amp; Push Notification
                </h3>
                <p className="text-[11px] text-gray-500">
                  SMTP Email + Simultaneous FCM Push to {selectedWinnerForEmail.email || selectedWinnerForEmail.username}
                </p>
              </div>
            </div>
            <button
              onClick={closeEmailModal}
              disabled={sendingEmail}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-lg"
            >
              <FiX size={18} />
            </button>
          </div>

          {/* Modal Form */}
          <form onSubmit={handleSendEmail} className="p-6 space-y-4">
            {/* Recipient Details Card */}
            <div className="bg-indigo-50/60 border border-indigo-100 rounded-xl p-3.5 space-y-1.5 text-xs">
              <div className="flex justify-between items-center">
                <span className="text-indigo-800 font-medium">Winner:</span>
                <span className="font-bold text-gray-900">
                  {selectedWinnerForEmail.username} (#{selectedWinnerForEmail.userId})
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-indigo-800 font-medium">Email Address:</span>
                <span className="font-mono text-gray-800 font-semibold truncate max-w-[240px]">
                  {selectedWinnerForEmail.email || "No email on record"}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-indigo-800 font-medium">Rank &amp; Event:</span>
                <span className="font-bold text-gray-900">
                  Rank #{selectedWinnerForEmail.userRank || 1} • {selectedEvent?.eventName || "Zeta Rush 1.0"}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-indigo-800 font-medium">Prize:</span>
                <span className="font-bold text-emerald-700">
                  {selectedWinnerForEmail.rewardZx || 0} ZX
                  {(selectedWinnerForEmail.coinAmount || selectedWinnerForEmail.rewardCoin?.coinAmount || selectedWinnerForEmail.rewardUsdt)
                    ? ` + ${selectedWinnerForEmail.coinAmount || selectedWinnerForEmail.rewardCoin?.coinAmount || selectedWinnerForEmail.rewardUsdt} ${selectedWinnerForEmail.coinSymbol || selectedWinnerForEmail.rewardCoin?.coinSymbol || 'USDT'}`
                    : ''}
                </span>
              </div>
            </div>

            {/* Template Options */}
            <div className="space-y-1.5">
              <label className="block text-xs font-bold text-gray-700">
                Quick Templates
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setEmailSubject("⚠️ Action Required: Important Warning");
                    setEmailMessage(
                      `Dear ${selectedWinnerForEmail?.username || "Winner"},\n\nWe noticed some unusual activity regarding your participation in "${selectedEvent?.eventName || "our latest event"}". Please reply to this email to verify your account, otherwise your rewards may be revoked.\n\nBest regards,\nZETA Network Team`
                    );
                  }}
                  className="px-3 py-1.5 bg-rose-50 text-rose-700 border border-rose-200 rounded-lg text-[11px] font-bold hover:bg-rose-100 transition cursor-pointer"
                >
                  1. Warning
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const eventName = selectedEvent?.eventName || "Event";
                    const userName = selectedWinnerForEmail?.username || "Winner";
                    const rank = selectedWinnerForEmail?.userRank ?? selectedWinnerForEmail?.rank ?? 1;
                    const coinAmt = selectedWinnerForEmail?.rewardCoin?.coin || selectedWinnerForEmail?.rewardCoin?.coinAmount || selectedWinnerForEmail?.coinAmount || selectedWinnerForEmail?.rewardUsdt || 0;
                    const coinSym = selectedWinnerForEmail?.rewardCoin?.coinSymbol || selectedWinnerForEmail?.coinSymbol || "USDT";

                    setEmailSubject(`Congratulations! You Won a ${coinSym} Prize — ${eventName}`);
                    setEmailMessage(
                      `Hello ${userName},\n\nCongratulations! 🎉\n\nWe’re pleased to inform you that you have been selected as a ${coinSym} prize winner in ${eventName}.\n\n🏆 Rank: #${rank}\n💰 Prize: ${coinAmt} ${coinSym}\n\nTo process your prize, please reply to this email with the following details:\n\nBinance UID:\nRegistered Email:\n\nPlease make sure the information provided is accurate to avoid delays in prize processing.\n\nOnce your details are verified, our team will proceed with the ${coinSym} transfer according to the event prize process.\n\nThank you for participating in ${eventName} and for being an active member of the community.\n\nCongratulations once again, and thank you for being part of the Zeta Network journey.\n\nBest regards,\nZeta Network Team`
                    );
                  }}
                  className="px-3 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg text-[11px] font-bold hover:bg-blue-100 transition cursor-pointer"
                >
                  2. Request Binance ID
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const eventName = selectedEvent?.eventName || "Event";
                    const userName = selectedWinnerForEmail?.username || selectedWinnerForEmail?.userName || "Winner";
                    const coinAmt = selectedWinnerForEmail?.rewardCoin?.coin || selectedWinnerForEmail?.rewardCoin?.coinAmount || selectedWinnerForEmail?.coinAmount || selectedWinnerForEmail?.rewardUsdt || 0;
                    const coinSym = selectedWinnerForEmail?.rewardCoin?.coinSymbol || selectedWinnerForEmail?.coinSymbol || "USDT";
                    const txnId = selectedWinnerForEmail?.txnId || selectedWinnerForEmail?.transactionId || "";
                    
                    setEmailSubject(`${eventName} — ${coinSym} Reward Transfer Confirmation`);
                    setEmailMessage(
                      `Hello ${userName},\n\nCongratulations once again on your achievement in ${eventName}! 🏆\n\nWe’re pleased to confirm that your ${coinSym} prize has been successfully processed.\n\n💰 ${coinSym} Reward: ${coinAmt} ${coinSym}\n🧾 Order ID: ${txnId}\n✅ Status: Successfully Transferred\n\nThe reward has been transferred to your Binance account. Please check your Binance account to confirm the received amount.\n\nThank you for participating in ${eventName} and for being an active member of the Zeta Network community. Your participation and support help us continue building a stronger community.\n\n📢 Support Our Winners\n\nThe official winner list is being shared across our social media channels. We invite you to visit the posts, congratulate the winners, and leave your genuine feedback and support.\n\nX: https://x.com/zeta__network\nInstagram: https://www.instagram.com/zeta__network?utm_source=ig_web_button_share_sheet&stkn=ZDNlZDc0MzIxNw==\nTelegram: https://t.me/zetanetworknews\n\nCongratulations once again, and thank you for being part of the Zeta Network journey.\n\nBest regards,\nZeta Network Team`
                    );
                  }}
                  className="px-3 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg text-[11px] font-bold hover:bg-emerald-100 transition cursor-pointer"
                >
                  3. Reward Won (Confirmation)
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const eventName = selectedEvent?.eventName || "Zeta Rush 1.0";
                    const userName = selectedWinnerForEmail?.username || selectedWinnerForEmail?.userName || "Winner";
                    const rank = selectedWinnerForEmail?.userRank ?? selectedWinnerForEmail?.rank ?? 1;
                    const zxAmt = selectedWinnerForEmail?.rewardZx || 0;

                    setEmailSubject(`🎉 Congratulations! Your Reward in ${eventName}`);
                    setEmailMessage(
                      `Dear ${userName},\n\nCongratulations on winning Rank #${rank} in "${eventName}"!\n\nYour reward of ${zxAmt} ZX has been approved and credited to your ZETA Network account.\n\nThank you for participating!\n\nBest regards,\nZETA Network Team`
                    );
                  }}
                  className="px-3 py-1.5 bg-gray-100 text-gray-800 border border-gray-300 rounded-lg text-[11px] font-bold hover:bg-gray-200 transition cursor-pointer"
                >
                  4. ZX Credited Template
                </button>
              </div>
            </div>

            {/* Email Subject / Notification Title Input */}
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">
                Subject / Notification Title <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                placeholder="e.g. 🎁 Action Required: Binance ID for Zeta Rush 1.0 Reward"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                className="w-full px-3.5 py-2 border border-gray-200 rounded-lg text-xs font-medium focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-gray-50 focus:bg-white transition"
                required
              />
            </div>

            {/* Email Message Content Textarea */}
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">
                Message Content (Dispatched as Email &amp; Push Notification) <span className="text-rose-500">*</span>
              </label>
              <textarea
                rows={6}
                placeholder="Enter congratulatory email and push notification message..."
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-xs leading-relaxed focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-gray-50 focus:bg-white transition font-sans"
                required
              />
            </div>

            {/* App Screen Route & External URL */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  App Screen Route (On Push Tap)
                </label>
                <select
                  value={emailRoute}
                  onChange={(e) => setEmailRoute(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400 cursor-pointer"
                >
                  {APP_ROUTES.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label} ({r.value})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  External URL (Optional Web Link)
                </label>
                <input
                  type="text"
                  placeholder="https://minezeta.com"
                  value={emailUrl}
                  onChange={(e) => setEmailUrl(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>
            </div>

            {/* Info notice about simultaneous dispatch */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 text-[11px] text-blue-800 leading-relaxed flex items-start gap-2">
              <span className="text-sm">🔔</span>
              <span>
                <strong>Simultaneous Dispatch</strong>: Clicking <b>Send Email &amp; Push</b> will dispatch the official Google SMTP email to <b>{selectedWinnerForEmail.email || selectedWinnerForEmail.username}</b> and simultaneously deliver a real-time FCM Push Notification to the user&apos;s mobile device, plus log it in their In-App Notification history.
              </span>
            </div>

            {/* Footer Actions */}
            <div className="pt-3 border-t border-gray-100 flex items-center justify-end gap-2.5">
              <button
                type="button"
                onClick={closeEmailModal}
                disabled={sendingEmail}
                className="px-4 py-2 text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={sendingEmail || !emailSubject.trim() || !emailMessage.trim()}
                className="px-5 py-2 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition flex items-center gap-1.5 shadow-xs disabled:opacity-50 cursor-pointer"
              >
                {sendingEmail ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Sending Email &amp; Push...</span>
                  </>
                ) : (
                  <>
                    <FiSend size={13} />
                    <span>🚀 Send Email &amp; Push Notification</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    )}

    {/* Send Custom Push Notification Modal */}
    {isPushModalOpen && selectedWinnerForPush && (
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center z-50 p-4"
        onClick={closePushModal}
      >
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-150"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-amber-50/50">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-800 flex items-center justify-center font-bold">
                <FiBell size={16} />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">
                  Send Push Notification to Winner
                </h3>
                <p className="text-[11px] text-gray-500">
                  FCM Push &amp; In-app Notification for {selectedWinnerForPush.username} ({selectedWinnerForPush.email || `#${selectedWinnerForPush.userId}`})
                </p>
              </div>
            </div>
            <button
              onClick={closePushModal}
              disabled={sendingPush}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-lg"
            >
              <FiX size={18} />
            </button>
          </div>

          {/* Modal Form */}
          <form onSubmit={handleSendPush} className="p-6 space-y-4">
            {/* Recipient Details Card */}
            <div className="bg-amber-50/60 border border-amber-100 rounded-xl p-3.5 space-y-1.5 text-xs">
              <div className="flex justify-between items-center">
                <span className="text-amber-800 font-medium">Winner:</span>
                <span className="font-bold text-gray-900">
                  {selectedWinnerForPush.username} (#{selectedWinnerForPush.userId})
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-amber-800 font-medium">Rank &amp; Event:</span>
                <span className="font-bold text-gray-900">
                  Rank #{selectedWinnerForPush.userRank || 1} • {selectedEvent?.eventName || "Zeta Rush 1.0"}
                </span>
              </div>
            </div>

            {/* Notification Title Input */}
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">
                Notification Title <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Enter notification title"
                value={pushTitle}
                onChange={(e) => setPushTitle(e.target.value)}
                className="w-full px-3.5 py-2 border border-gray-200 rounded-lg text-xs font-medium focus:outline-none focus:ring-2 focus:ring-amber-400 bg-gray-50 focus:bg-white transition"
                required
              />
            </div>

            {/* Notification Message Textarea */}
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">
                Notification Message <span className="text-rose-500">*</span>
              </label>
              <textarea
                rows={3}
                placeholder="Enter notification message..."
                value={pushMessage}
                onChange={(e) => setPushMessage(e.target.value)}
                className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-xs leading-relaxed focus:outline-none focus:ring-2 focus:ring-amber-400 bg-gray-50 focus:bg-white transition"
                required
              />
            </div>

            {/* Target App Screen Route */}
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">
                📱 App Screen Route (Deep Link on Tap)
              </label>
              <select
                value={pushRoute}
                onChange={(e) => setPushRoute(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs font-medium focus:outline-none focus:ring-2 focus:ring-amber-400 bg-gray-50 focus:bg-white transition cursor-pointer"
              >
                {APP_ROUTES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label} ({r.value})
                  </option>
                ))}
              </select>
            </div>

            {/* Optional External URL */}
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">
                🌐 External URL <span className="text-gray-400 font-normal">(Optional Web Link)</span>
              </label>
              <input
                type="text"
                placeholder="https://minezeta.com/announcements"
                value={pushUrl}
                onChange={(e) => setPushUrl(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs font-mono focus:outline-none focus:ring-2 focus:ring-amber-400 bg-gray-50 focus:bg-white transition"
              />
            </div>

            {/* Footer Actions */}
            <div className="pt-3 border-t border-gray-100 flex items-center justify-end gap-2.5">
              <button
                type="button"
                onClick={closePushModal}
                disabled={sendingPush}
                className="px-4 py-2 text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={sendingPush || !pushTitle.trim() || !pushMessage.trim()}
                className="px-5 py-2 text-xs font-bold text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition flex items-center gap-1.5 shadow-xs disabled:opacity-50 cursor-pointer"
              >
                {sendingPush ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Sending Push...</span>
                  </>
                ) : (
                  <>
                    <FiSend size={13} />
                    <span>Send Push Notification</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    )}

    {/* Send Bulk Push Notification Modal */}
    {isBulkPushModalOpen && (
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-4 overflow-y-auto"
        onClick={closeBulkPushModal}
      >
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150 my-6 max-h-[92vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-amber-50 via-yellow-50 to-amber-50 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500 text-black flex items-center justify-center font-black shadow-xs">
                <FiBell size={20} />
              </div>
              <div>
                <h3 className="text-base font-black text-gray-900 flex items-center gap-2">
                  <span>Bulk Push Notification</span>
                  <span className="text-xs bg-amber-100 text-amber-900 font-bold px-2 py-0.5 rounded-md border border-amber-300">
                    {selectedEvent?.eventName || "Event Winners"}
                  </span>
                </h3>
                <p className="text-[11px] text-gray-500 mt-0.5">
                  Broadcast push notifications directly to multiple reward winners with dynamic personalization
                </p>
              </div>
            </div>
            <button
              onClick={closeBulkPushModal}
              disabled={sendingBulkPush}
              className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg transition disabled:opacity-40 cursor-pointer"
            >
              <FiX size={20} />
            </button>
          </div>

          {/* Modal Form */}
          <form onSubmit={handleSendBulkPush} className="p-6 space-y-4 overflow-y-auto flex-1">
            {/* 1. Target Audience Selection */}
            <div>
              <label className="block text-xs font-bold text-gray-800 mb-2 flex items-center justify-between">
                <span>🎯 Select Target Audience (Winners)</span>
                <span className="text-[11px] text-gray-500 font-normal">
                  Choose which winners will receive this notification
                </span>
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                {/* All 500 Winners */}
                <button
                  type="button"
                  disabled={sendingBulkPush}
                  onClick={() => setBulkTargetAudience("all")}
                  className={`p-3 rounded-xl border text-left transition flex flex-col justify-between cursor-pointer ${
                    bulkTargetAudience === "all"
                      ? "bg-amber-50 border-amber-400 ring-2 ring-amber-400/30 text-gray-900 shadow-xs"
                      : "bg-white border-gray-200 hover:border-gray-300 text-gray-700"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold">Top {maxWinners} Winners</span>
                    <span className="text-sm">🏆</span>
                  </div>
                  <span className="text-lg font-black mt-1 text-gray-900">
                    {totalRecords}
                  </span>
                  <span className="text-[10px] text-gray-400 mt-0.5">
                    Qualified prize winners
                  </span>
                </button>

                {/* Pending Approvals */}
                <button
                  type="button"
                  disabled={sendingBulkPush}
                  onClick={() => setBulkTargetAudience("pending")}
                  className={`p-3 rounded-xl border text-left transition flex flex-col justify-between cursor-pointer ${
                    bulkTargetAudience === "pending"
                      ? "bg-amber-50 border-amber-400 ring-2 ring-amber-400/30 text-gray-900 shadow-xs"
                      : "bg-white border-gray-200 hover:border-gray-300 text-gray-700"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold">Pending Only</span>
                    <span className="text-sm">⏳</span>
                  </div>
                  <span className="text-lg font-black mt-1 text-amber-600">
                    {pendingCount}
                  </span>
                  <span className="text-[10px] text-gray-400 mt-0.5">
                    Awaiting approval
                  </span>
                </button>

                {/* Credited Rewards */}
                <button
                  type="button"
                  disabled={sendingBulkPush}
                  onClick={() => setBulkTargetAudience("credited")}
                  className={`p-3 rounded-xl border text-left transition flex flex-col justify-between cursor-pointer ${
                    bulkTargetAudience === "credited"
                      ? "bg-amber-50 border-amber-400 ring-2 ring-amber-400/30 text-gray-900 shadow-xs"
                      : "bg-white border-gray-200 hover:border-gray-300 text-gray-700"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold">Credited Only</span>
                    <span className="text-sm">✅</span>
                  </div>
                  <span className="text-lg font-black mt-1 text-emerald-600">
                    {creditedCount}
                  </span>
                  <span className="text-[10px] text-gray-400 mt-0.5">
                    Rewards confirmed
                  </span>
                </button>

                {/* Custom Selected in Table */}
                <button
                  type="button"
                  disabled={sendingBulkPush || selectedHistoryIds.size === 0}
                  onClick={() => setBulkTargetAudience("selected")}
                  className={`p-3 rounded-xl border text-left transition flex flex-col justify-between ${
                    bulkTargetAudience === "selected"
                      ? "bg-amber-50 border-amber-400 ring-2 ring-amber-400/30 text-gray-900 shadow-xs cursor-pointer"
                      : selectedHistoryIds.size > 0
                      ? "bg-white border-gray-200 hover:border-gray-300 text-gray-700 cursor-pointer"
                      : "bg-gray-50 border-gray-100 text-gray-400 cursor-not-allowed opacity-50"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold">Selected</span>
                    <span className="text-sm">☑️</span>
                  </div>
                  <span className="text-lg font-black mt-1 text-blue-600">
                    {selectedHistoryIds.size}
                  </span>
                  <span className="text-[10px] text-gray-400 mt-0.5">
                    Checked in table
                  </span>
                </button>
              </div>
            </div>

            {/* Dynamic Placeholders Tag Helpers */}
            <div className="bg-amber-50/50 border border-amber-200/80 rounded-xl p-3">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[11px] font-bold text-amber-900">
                  💡 Dynamic Variables (Click variable to append into message):
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { tag: "{username}", desc: "Winner username" },
                  { tag: "{rank}", desc: "Rank number" },
                  { tag: "{reward}", desc: "Prize (ZX + Crypto)" },
                  { tag: "{rewardZx}", desc: "ZX amount" },
                  { tag: "{rewardCrypto}", desc: "Crypto USDT" },
                  { tag: "{eventName}", desc: "Event name" },
                ].map((item) => (
                  <button
                    key={item.tag}
                    type="button"
                    onClick={() => insertTagIntoMessage(item.tag)}
                    className="inline-flex items-center gap-1 bg-white hover:bg-amber-100 hover:text-amber-900 border border-amber-200 text-amber-900 px-2.5 py-1 rounded-lg text-xs font-mono font-semibold transition shadow-2xs cursor-pointer"
                    title={`Click to insert ${item.tag} (${item.desc})`}
                  >
                    <span>{item.tag}</span>
                    <span className="text-[10px] text-gray-400 font-sans font-normal">
                      ({item.desc})
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Notification Title Input */}
            <div>
              <label className="block text-xs font-bold text-gray-800 mb-1">
                Notification Title <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                placeholder="e.g. 🎉 Congratulations on your reward in {eventName}!"
                value={bulkPushTitle}
                onChange={(e) => setBulkPushTitle(e.target.value)}
                className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-amber-400 bg-gray-50 focus:bg-white transition"
                required
                disabled={sendingBulkPush}
              />
            </div>

            {/* Notification Message Textarea */}
            <div>
              <label className="block text-xs font-bold text-gray-800 mb-1">
                Notification Message / Body <span className="text-rose-500">*</span>
              </label>
              <textarea
                rows={3}
                placeholder="e.g. Congratulations {username}! Your reward of {reward} for Rank #{rank} in '{eventName}' is ready. Tap to view!"
                value={bulkPushMessage}
                onChange={(e) => setBulkPushMessage(e.target.value)}
                className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-xs leading-relaxed focus:outline-none focus:ring-2 focus:ring-amber-400 bg-gray-50 focus:bg-white transition resize-none"
                required
                disabled={sendingBulkPush}
              />
            </div>

            {/* Deep Link Route & URL Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  📱 App Screen Route (Deep Link on Tap)
                </label>
                <select
                  value={bulkPushRoute}
                  onChange={(e) => setBulkPushRoute(e.target.value)}
                  disabled={sendingBulkPush}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-xs font-medium focus:outline-none focus:ring-2 focus:ring-amber-400 bg-gray-50 focus:bg-white transition cursor-pointer"
                >
                  {APP_ROUTES.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label} ({r.value})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  🌐 External URL <span className="text-gray-400 font-normal">(Optional Web Link)</span>
                </label>
                <input
                  type="text"
                  placeholder="https://minezeta.com"
                  value={bulkPushUrl}
                  onChange={(e) => setBulkPushUrl(e.target.value)}
                  disabled={sendingBulkPush}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-xs font-mono focus:outline-none focus:ring-2 focus:ring-amber-400 bg-gray-50 focus:bg-white transition"
                />
              </div>
            </div>

            {/* Live Smartphone Notification Preview */}
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">
                📱 Live Recipient Mobile Preview (Sample Winner: @{samplePreviewWinner.username || "user"})
              </label>
              <div className="bg-slate-900 text-white rounded-2xl p-3.5 shadow-inner border border-slate-800">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-amber-500 text-black flex items-center justify-center font-black flex-shrink-0 text-sm shadow-md">
                    Z
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10px] font-bold text-amber-400 tracking-wide uppercase">
                        ZETA NETWORK • NOW
                      </span>
                      <span className="text-[10px] text-gray-400 font-mono">
                        {bulkPushRoute}
                      </span>
                    </div>
                    <h4 className="text-xs font-bold text-white mt-0.5 truncate">
                      {formatPushTemplate(
                        bulkPushTitle || "Reward Notification",
                        samplePreviewWinner,
                        selectedEvent?.eventName
                      )}
                    </h4>
                    <p className="text-[11px] text-gray-300 mt-0.5 leading-snug break-words">
                      {formatPushTemplate(
                        bulkPushMessage || "Congratulations on winning!",
                        samplePreviewWinner,
                        selectedEvent?.eventName
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Real-time Sending Progress Bar */}
            {sendingBulkPush && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2.5 animate-in fade-in">
                <div className="flex items-center justify-between text-xs font-bold text-amber-900">
                  <span className="flex items-center gap-1.5">
                    <div className="w-3.5 h-3.5 border-2 border-amber-700 border-t-transparent rounded-full animate-spin" />
                    <span>Broadcasting Push Notifications...</span>
                  </span>
                  <span>
                    {bulkPushProgress.current} / {bulkPushProgress.total} ({bulkPushProgress.percent}%)
                  </span>
                </div>

                {/* Progress Bar */}
                <div className="w-full bg-amber-200 rounded-full h-2.5 overflow-hidden">
                  <div
                    className="bg-amber-600 h-full rounded-full transition-all duration-200"
                    style={{ width: `${bulkPushProgress.percent}%` }}
                  />
                </div>

                <div className="flex items-center justify-between text-[11px] text-amber-800">
                  <span className="truncate max-w-[260px]">
                    {bulkPushProgress.currentName
                      ? `👉 Sending to @${bulkPushProgress.currentName}...`
                      : "Processing..."}
                  </span>
                  <span className="font-semibold">
                    ✅ {bulkPushProgress.successCount} Sent | ❌ {bulkPushProgress.failCount} Failed
                  </span>
                </div>
              </div>
            )}

            {/* Modal Footer Actions */}
            <div className="pt-3 border-t border-gray-100 flex items-center justify-end gap-2.5 flex-shrink-0">
              <button
                type="button"
                onClick={closeBulkPushModal}
                disabled={sendingBulkPush}
                className="px-4 py-2 text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition cursor-pointer disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={
                  sendingBulkPush ||
                  !bulkPushTitle.trim() ||
                  !bulkPushMessage.trim() ||
                  (bulkTargetAudience === "selected" && selectedHistoryIds.size === 0)
                }
                className="px-5 py-2.5 text-xs font-black text-black bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 rounded-xl transition flex items-center gap-2 shadow-sm disabled:opacity-40 cursor-pointer"
              >
                {sendingBulkPush ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                    <span>Sending ({bulkPushProgress.percent}%)...</span>
                  </>
                ) : (
                  <>
                    <FiSend size={14} />
                    <span>
                      Send Push to{" "}
                      {bulkTargetAudience === "selected"
                        ? `${selectedHistoryIds.size} Selected`
                        : bulkTargetAudience === "pending"
                        ? `${pendingCount} Pending`
                        : bulkTargetAudience === "credited"
                        ? `${creditedCount} Credited`
                        : `All ${totalRecords} Winners`}
                    </span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    )}
  </div>
);
};

export default EventWinners;



